/**
 * EMZI NEXUS V6.3 - Language Context
 * Global language and tone management with ATS integration
 */

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import msStrings from '../locales/ms.json';
import urbanStrings from '../locales/urban.json';
import enStrings from '../locales/en.json';
import { BACKEND_URL } from '../utils/backendUrl';


const LanguageContext = createContext();

const locales = {
  bm: msStrings,
  urban: urbanStrings,
  en: enStrings
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('urban'); // Default to Urban (Bahasa Malaysia Urban)
  const [tone, setTone] = useState('santun'); // Default tone
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState(null);

  // Load user preferences from localStorage on mount
  useEffect(() => {
    // Check if reload was triggered by language change
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('langRefresh')) {
      // Remove query param
      window.history.replaceState(null, '', window.location.pathname);
    }

    // Check if preference is stale (>12h)
    const savedLang = localStorage.getItem('appLocale');
    const savedLangTs = localStorage.getItem('appLocale_ts');
    const savedTone = localStorage.getItem('userTone');
    
    const now = Date.now();
    const isStale = savedLangTs && (now - parseInt(savedLangTs)) > (12 * 60 * 60 * 1000);
    
    if (savedLang && ['bm', 'urban', 'en'].includes(savedLang) && !isStale) {
      setLanguage(savedLang);
    }
    if (savedTone && ['santun', 'formal', 'casual'].includes(savedTone)) {
      setTone(savedTone);
    }
    
    // Re-sync from server if stale or missing
    if (isStale || !savedLang) {
      // Will be handled by loadUserPreferences if user is authenticated
    }
  }, []);

  // Load user preferences from backend (if authenticated)
  const loadUserPreferences = useCallback(async (userId) => {
    if (!userId) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/lang/prefs/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setLanguage(data.languagePreference || 'urban');
        setTone(data.tonePreference || 'santun');
        // Save to localStorage
        localStorage.setItem('userLanguage', data.languagePreference || 'urban');
        localStorage.setItem('userTone', data.tonePreference || 'santun');
      }
    } catch (error) {
      console.error('Failed to load language preferences:', error);
    }
  }, []);

  // Request language change (shows modal)
  const requestLanguageChange = useCallback((newLang) => {
    if (!['bm', 'urban', 'en'].includes(newLang)) return;
    if (newLang === language) return; // No-op if same language
    
    setPendingLanguage(newLang);
    setShowModal(true);
  }, [language]);

  // Confirm language change (triggers reload)
  const confirmLanguageChange = useCallback(async (userId = null) => {
    if (!pendingLanguage) return;
    
    setLoading(true);
    const oldLang = language;
    const newLang = pendingLanguage;
    
    try {
      // 1. Save to backend (with ISO audit)
      if (userId) {
        const response = await fetch(`${BACKEND_URL}/api/lang/prefs/${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            languagePreference: newLang,
            _audit: {
              old_language: oldLang,
              new_language: newLang,
              timestamp: new Date().toISOString()
            }
          })
        });
        
        if (!response.ok) {
          throw new Error('Network failure');
        }
      }
      
      // 2. Save to localStorage with timestamp
      localStorage.setItem('appLocale', newLang);
      localStorage.setItem('appLocale_ts', Date.now().toString());
      localStorage.setItem('userTone', tone);
      
      // 3. Clear stale cache keys
      localStorage.removeItem('i18n_cache');
      localStorage.removeItem('intl_messages');
      localStorage.removeItem('langRefresh');
      
      // 4. Dispatch event for hot-swap components
      window.dispatchEvent(new CustomEvent('langChange', { 
        detail: { language: newLang, oldLanguage: oldLang } 
      }));
      
      // 5. Force reload with query param (authoritative)
      setTimeout(() => {
        window.location.replace(window.location.pathname + '?langRefresh=1');
      }, 100);
      
    } catch (error) {
      console.error('Failed to save language preference:', error);
      setLoading(false);
      setShowModal(false);
      setPendingLanguage(null);
      // Show error toast (will be handled by component)
      throw error;
    }
  }, [pendingLanguage, language, tone]);

  // Cancel language change
  const cancelLanguageChange = useCallback(() => {
    setShowModal(false);
    setPendingLanguage(null);
  }, []);

  // Legacy method for backward compatibility (direct change without modal)
  const changeLanguage = useCallback(async (newLang, userId = null) => {
    if (!['bm', 'urban', 'en'].includes(newLang)) return;
    
    setLanguage(newLang);
    localStorage.setItem('appLocale', newLang);
    localStorage.setItem('appLocale_ts', Date.now().toString());
    
    // Update on backend if user is authenticated
    if (userId) {
      try {
        await fetch(`${BACKEND_URL}/api/lang/prefs/${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ languagePreference: newLang })
        });
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
  }, []);

  // Update tone preference
  const changeTone = useCallback(async (newTone, userId = null) => {
    if (!['santun', 'formal', 'casual'].includes(newTone)) return;
    
    setLoading(true);
    setTone(newTone);
    localStorage.setItem('userTone', newTone);
    
    // Update on backend if user is authenticated
    if (userId) {
      try {
        await fetch(`${BACKEND_URL}/api/lang/prefs/${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tonePreference: newTone })
        });
      } catch (error) {
        console.error('Failed to save tone preference:', error);
      }
    }
    
    setLoading(false);
  }, []);

  // Get translated string by key path (e.g., "common.save")
  // Returns RAW values (string | array | object) without conversion
  const t = useCallback((key, fallback = null) => {
    const keys = key.split('.');
    let value = locales[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return fallback !== null ? fallback : key; // Fallback to provided fallback or key
      }
    }
    
    // Return raw value (string, array, object, etc.) without conversion
    return value !== undefined && value !== null ? value : (fallback !== null ? fallback : key);
  }, [language]);

  // Listen for language change events
  useEffect(() => {
    const handleLangChange = (event) => {
      const { language: newLang } = event.detail;
      if (newLang && ['bm', 'urban', 'en'].includes(newLang)) {
        setLanguage(newLang);
      }
    };
    
    window.addEventListener('langChange', handleLangChange);
    return () => window.removeEventListener('langChange', handleLangChange);
  }, []);

  const value = {
    language,
    tone,
    loading,
    showModal,
    pendingLanguage,
    changeLanguage,
    requestLanguageChange,
    confirmLanguageChange,
    cancelLanguageChange,
    changeTone,
    loadUserPreferences,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Shorthand hook for translation only
export const useLang = () => {
  const { t } = useLanguage();
  return t;
};

export default LanguageContext;
